import React from 'react';
import Flag from 'components/Flag';
import { getFormatter } from 'shared/utils/formatter';
import { getDimensionRawValue, getDisplayValue } from 'shared/utils/dimensionValue';
import useFormatMessage from 'hooks/useFormatMessage';
import { useMatomoGoalNames, isGoalDimension } from 'hooks/useMatomoGoalNames';
import { getMatomoGoalLabel, isDeletedMatomoGoal, formatDeletedGoalLabel } from 'hooks/useMatomoGoalOptions';
import globalMessages from 'translations/messages';
import { Popover, Space } from 'antd';
import MiddleEllipsis from 'components/MiddleEllipsis';
import TechnologyIcon, { hasTechnologyIcons } from 'components/TechnologyIcon';
import DimensionLabel from 'components/DimensionLabel';
import { DeviceTypeIcon } from 'components/DeviceTypeIcons';
import messages from './messages';

const translatedDimensions = ['deviceType', 'deviceModel', 'referrerType', 'referrerSocialNetwork', 'visitCountGrouped', 'visitDaysSinceLastVisitGrouped'];
const booleanDimensions = ['browserCookies'];

export const getDimensionValue = ({ $t, dimension, value: rawValue, row, maxLength, goalNames, goalNamesLoading, html }) => {
  const value = getDisplayValue(rawValue);
  if (row?.other) {
    return $t(messages.other);
  }

  if (row?.loading) {
    return $t(globalMessages.loading);
  }

  if ((value == null || value === '')) {
    if (dimension.emptyLabel) {
      return dimension.emptyLabel;
    }
    if (dimension.type === 'path' || dimension.type === 'pathSegment') {
      return '/';
    }
    return $t(messages.unknown);
  }

  if (translatedDimensions.includes(dimension.dimension)) {
    if (messages[value]) {
      return $t(messages[value]);
    }
    if (messages[`${dimension.dimension}${value}`]) {
      return $t(messages[`${dimension.dimension}${value}`]);
    }
  }

  if (booleanDimensions.includes(dimension.dimension)) {
    return value ? $t(messages.yes) : $t(messages.no);
  }

  if (isGoalDimension(dimension) && value != null) {
    const deletedLabel = $t(messages.deletedGoal);
    const name = getMatomoGoalLabel(goalNames, value, {
      deletedLabel,
      loading: goalNamesLoading,
    });
    if (name !== undefined) {
      let display = maxLength && name.length > maxLength
        ? `${name.slice(0, maxLength)}...`
        : name;
      if (isDeletedMatomoGoal(goalNames, value, goalNamesLoading)) {
        display = formatDeletedGoalLabel(display, { html });
      }
      return display;
    }
    if (goalNamesLoading) {
      return $t(globalMessages.loading);
    }
  }

  const formattedValue = getFormatter(dimension.type)(value);
  if (maxLength && formattedValue.length > maxLength) {
    return `${formattedValue.slice(0, maxLength)}...`;
  }
  return formattedValue;
};

const DimensionValue = ({
  dimension,
  value,
  row,
  truncate,
  /** Raw value for popover body when it should differ from `value` (e.g. full path vs path segment). */
  popoverValue,
  /** When true, MiddleEllipsis has no fixed width (parent flex layout supplies bounds). */
  ellipsisContainerBound,
  wrapperClassName,
  popoverStyles: popoverStylesProp,
  popoverClassNames: popoverClassNamesProp,
  popoverContentClassName,
  mouseEnterDelay = 0.1,
}) => {
  const $t = useFormatMessage();
  const { goalNames, loading: goalNamesLoading } = useMatomoGoalNames();

  const rawDimensionValue = getDimensionValue({ $t, dimension, value, row, goalNames, goalNamesLoading });
  let dimensionValue = rawDimensionValue === $t(messages.unknown)
    || (isGoalDimension(dimension) && isDeletedMatomoGoal(goalNames, value, goalNamesLoading))
    ? <em>{rawDimensionValue}</em>
    : rawDimensionValue;
  const countryCode = getDimensionRawValue(value, row?.[dimension.name]);
  const flag = ['locale', 'language'].includes(dimension.type) && typeof countryCode === 'string'
    ? <Flag locale={countryCode} />
    : dimension.type === 'country' && /^[a-z]{2}$/i.test(countryCode || '')
      ? <Flag countryCode={countryCode} /> : null;
  if (flag && !row?.other && !row?.loading) {
    dimensionValue = (
      <Space>
        {flag}
        {dimensionValue}
      </Space>
    );
  }

  const rawPopoverDisplayValue = popoverValue !== undefined
    ? getDimensionValue({ $t, dimension, value: popoverValue, row, goalNames, goalNamesLoading })
    : rawDimensionValue;
  const popoverDisplayValue = rawPopoverDisplayValue === $t(messages.unknown)
    || (isGoalDimension(dimension) && isDeletedMatomoGoal(goalNames, popoverValue ?? value, goalNamesLoading))
    ? <em>{rawPopoverDisplayValue}</em>
    : rawPopoverDisplayValue;

  const icon = row?.icon || (!row?.other && !row?.loading && dimension.type === 'deviceType'
    ? <DeviceTypeIcon value={getDisplayValue(value)} /> : null);

  if (icon) {
    return (
      <Space>
        {icon}
        {dimensionValue}
      </Space>
    );
  }

  if (!row?.other && !row?.loading && hasTechnologyIcons(dimension.type)) {
    return (
      <Space>
        <TechnologyIcon type={dimension.type} value={value} />
        {dimensionValue}
      </Space>
    );
  }

  const useTruncatePopover = truncate !== false && (truncate === true || !!dimension.truncate);
  if (!useTruncatePopover) return dimensionValue;

  const middleEllipsisWidth = ellipsisContainerBound
    ? undefined
    : (dimension.width || 300);
  const middleEllipsisProps = middleEllipsisWidth != null
    ? { width: middleEllipsisWidth }
    : {};

  const popoverContent = popoverContentClassName ? (
    <div className={popoverContentClassName}>{popoverDisplayValue}</div>
  ) : popoverDisplayValue;

  return (
    <Popover
      title={(
        <DimensionLabel
          name={dimension.name}
          dimension={dimension.field || dimension.dimension}
          label={dimension.label}
          description={dimension.description}
          lineBreaks={false}
        />
      )}
      content={popoverContent}
      trigger="hover"
      mouseEnterDelay={mouseEnterDelay}
      classNames={popoverClassNamesProp}
      styles={{
        root: {
          minWidth: 250,
          maxWidth: 450,
          ...(popoverStylesProp?.root || {}),
        },
        body: {
          overflowWrap: 'break-word',
          ...(popoverStylesProp?.body || {}),
        },
      }}
    >
      <div className={wrapperClassName}>
        <MiddleEllipsis {...middleEllipsisProps}>
          <span className="constrainedChild">
            <span className="ellipseMe">{dimensionValue}</span>
          </span>
        </MiddleEllipsis>
      </div>
    </Popover>

  );
};

export default DimensionValue;
